import { useRef, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { HandHeart, Home, MapPin, Package, Recycle, Sprout, Sun, Wheat, X } from "lucide-react";
import { IMAGES } from "../lib/images";
import { FadeUp, SectionHeading } from "./Reveal";

const STAGE_ICONS = [Sprout, Sun, Wheat, Package, Home];

export function Journey() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.65"] });
  const progress = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  return (
    <section id="journey" data-testid="farm-journey-section" className="relative bg-nightgreen py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          dark
          center
          testId="journey-heading"
          eyebrow="From Farm to You"
          title="The Journey of Every Harvest"
          subtitle="Five careful steps stand between our soil and your home."
        />

        <div ref={ref} className="relative mt-16 sm:mt-20 max-w-5xl mx-auto">
          <div className="absolute left-5 md:left-1/2 top-0 bottom-0 w-px bg-cream/10 md:-translate-x-1/2" aria-hidden="true" />
          <motion.div
            style={{ scaleY: progress }}
            className="absolute left-5 md:left-1/2 top-0 bottom-0 w-[3px] -translate-x-1/2 origin-top bg-gradient-to-b from-gold via-goldlight to-gold rounded-full"
            aria-hidden="true"
          />

          <div className="space-y-14 md:space-y-20">
            {IMAGES.journey.map((stage, i) => {
              const Icon = STAGE_ICONS[i];
              const left = i % 2 === 0;
              return (
                <div key={stage.title} data-testid={`journey-stage-${i}`} className="relative md:grid md:grid-cols-2 md:gap-16 items-center">
                  <div className="absolute left-5 md:left-1/2 -translate-x-1/2 w-11 h-11 rounded-full bg-deepforest border border-gold/50 text-goldlight flex items-center justify-center z-10 shadow-[0_0_30px_rgba(200,157,84,0.25)]">
                    <Icon className="w-5 h-5" />
                  </div>
                  <FadeUp className={`pl-16 md:pl-0 ${left ? "md:pr-16 md:text-right md:order-1" : "md:pl-16 md:order-2"}`}>
                    <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold">Step 0{i + 1}</span>
                    <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold text-cream">{stage.title}</h3>
                    <p className="mt-3 text-sm sm:text-base font-light text-cream/70 leading-relaxed">{stage.text}</p>
                  </FadeUp>
                  <FadeUp delay={0.15} className={`pl-16 md:pl-0 mt-5 md:mt-0 ${left ? "md:order-2" : "md:order-1"}`}>
                    <div className="rounded-2xl overflow-hidden border border-cream/10 shadow-xl group">
                      <img
                        src={stage.img}
                        alt={stage.title}
                        loading="lazy"
                        className="w-full h-52 sm:h-64 object-cover transition-transform duration-[1300ms] group-hover:scale-105"
                      />
                    </div>
                  </FadeUp>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

const WHY_ITEMS = [
  { icon: Sun, title: "Naturally Grown", text: "Crops raised the slow way — with sun, soil, water and patience." },
  { icon: HandHeart, title: "Handpicked With Care", text: "Every harvest is picked, cleaned and packed by hand at the farm." },
  { icon: MapPin, title: "Farm Direct to You", text: "No middlemen. Enquire directly and buy from the people who grow it." },
  { icon: Recycle, title: "Rooted in Sustainability", text: "Practices that keep the soil alive for the next season — and the next generation." },
];

export function WhyChoose() {
  return (
    <section data-testid="why-choose-us-section" className="bg-cream py-24 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          center
          testId="why-choose-heading"
          eyebrow="Why Choose Us"
          title="Why Choose Unique Organic Farm"
          subtitle="Simple reasons, honestly grown."
        />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY_ITEMS.map((item, i) => (
            <FadeUp key={item.title} delay={i * 0.1} className="h-full">
              <div
                data-testid={`why-card-${i}`}
                className="h-full rounded-2xl bg-beige border border-sand p-7 transition-all duration-500 hover:border-gold/60 hover:-translate-y-1.5 hover:shadow-[0_18px_50px_rgba(17,38,29,0.1)]"
              >
                <span className="inline-flex w-12 h-12 rounded-full bg-forest text-goldlight items-center justify-center">
                  <item.icon className="w-5.5 h-5.5" />
                </span>
                <h3 className="mt-5 font-serif text-xl font-semibold text-forest">{item.title}</h3>
                <p className="mt-2.5 text-sm font-light text-muted leading-relaxed">{item.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Gallery() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="gallery" data-testid="farm-gallery-section" className="bg-beige py-24 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          center
          testId="gallery-heading"
          eyebrow="Life at the Farm"
          title="Moments From Our Fields"
          subtitle="Sunrises, soil, harvests and the everyday beauty of farm life."
        />
        <div className="mt-14 columns-2 md:columns-3 lg:columns-4 gap-4">
          {IMAGES.gallery.map((g, i) => (
            <FadeUp key={i} delay={(i % 4) * 0.07} className="mb-4 break-inside-avoid">
              <button
                data-testid={`gallery-img-${i}`}
                onClick={() => setLightbox(i)}
                className="group block w-full rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-gold"
                aria-label={`View photo: ${g.alt}`}
              >
                <img
                  src={g.img}
                  alt={g.alt}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-[1200ms] group-hover:scale-108 group-hover:scale-105"
                />
              </button>
            </FadeUp>
          ))}
        </div>
      </div>

      {lightbox !== null && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[110] bg-nightgreen/95 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="gallery-close-btn"
            aria-label="Close gallery"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-cream/10 text-cream flex items-center justify-center hover:bg-cream/25 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <motion.img
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            src={IMAGES.gallery[lightbox].img}
            alt={IMAGES.gallery[lightbox].alt}
            className="max-h-[85vh] max-w-full rounded-xl shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
